import JSZip from 'jszip';
import * as XLSX from 'xlsx';

/**
 * API 객체 배열을 키 그대로 엑셀 행으로 저장 (레거시 exportObjectRowsToXlsxFile).
 * 수수료 목록 엑셀 등 headerMap 없이 raw 키가 필요한 경우에 사용.
 */
export const exportObjectRowsToXlsxFile = (
  rows: Record<string, unknown>[],
  filename = 'export.xlsx',
  sheetName = 'commission',
) => {
  const safeName =
    String(sheetName)
      .replace(/[\\/?*[\]:]/g, '_')
      .replace(/^'+|'+$/g, '')
      .trim()
      .slice(0, 31) || 'commission';

  const outName = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;

  if (!rows.length) {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([['(데이터 없음)']]);
    XLSX.utils.book_append_sheet(wb, ws, safeName);
    XLSX.writeFile(wb, outName);
    return;
  }

  const keys: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
  }

  const aoa: unknown[][] = [
    keys,
    ...rows.map((row) =>
      keys.map((key) => {
        const value = row[key];
        if (value == null) return '';
        if (typeof value === 'object') {
          try {
            return JSON.stringify(value);
          } catch {
            return String(value);
          }
        }
        return value;
      }),
    ),
  ];

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  XLSX.utils.book_append_sheet(workbook, worksheet, safeName);
  XLSX.writeFile(workbook, outName);
};

/** 객체 배열을 XLSX 파일로 다운로드 (레거시 exportToXLSX 대체) */
export const downloadXlsx = (
  rows: Record<string, unknown>[],
  filename: string,
  headerMap?: Record<string, string>,
) => {
  if (rows.length === 0) return;

  const exportRows = headerMap
    ? rows.map((row) => {
        const mapped: Record<string, unknown> = {};
        Object.entries(headerMap).forEach(([key, label]) => {
          mapped[label] = row[key];
        });
        return mapped;
      })
    : rows;

  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(
    workbook,
    filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`,
  );
};

export interface XlsxSheetExport {
  sheetName: string;
  data: Record<string, unknown>[];
}

/**
 * 여러 시트를 한 워크북으로 내보내기 — 레거시 `exportSheetsToXLSX`.
 * 정산 정보 생성기: 전체내역 + 제약사별 시트.
 */
export const exportSheetsToXlsx = (
  sheets: XlsxSheetExport[],
  filename = 'data',
  headerMap?: Record<string, string>,
) => {
  const workbook = XLSX.utils.book_new();
  const usedSheetNames = new Set<string>();

  const sanitizeSheetName = (sheetName = 'Sheet') => {
    const normalizedName =
      String(sheetName)
        .replace(/[\\/?*[\]:]/g, ' ')
        .replace(/^'+|'+$/g, '')
        .replace(/\s+/g, ' ')
        .trim() || 'Sheet';

    const maxLength = 31;
    let candidate = normalizedName.slice(0, maxLength);
    let counter = 1;

    while (usedSheetNames.has(candidate)) {
      const suffix = ` (${counter})`;
      const baseLength = maxLength - suffix.length;
      candidate = `${normalizedName.slice(0, Math.max(baseLength, 1))}${suffix}`;
      counter += 1;
    }

    usedSheetNames.add(candidate);
    return candidate;
  };

  sheets.forEach(({ sheetName, data = [] }) => {
    let exportData: Record<string, unknown>[] = data;
    let headers: string[] | undefined;

    if (headerMap) {
      exportData = data.map((item) => {
        const mappedItem: Record<string, unknown> = {};
        Object.entries(headerMap).forEach(([key, label]) => {
          mappedItem[label] = item[key];
        });
        return mappedItem;
      });
      headers = Object.values(headerMap);
    }

    const worksheet = XLSX.utils.json_to_sheet(exportData, { header: headers });
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      sanitizeSheetName(sheetName),
    );
  });

  XLSX.writeFile(
    workbook,
    filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`,
  );
};

/** 헤더만 있는 빈 XLSX 템플릿 */
export const exportXlsxTemplate = (
  headerMap: Record<string, string>,
  filename = 'template',
) => {
  const exportData: Record<string, string>[] = [{}];
  const headers = Object.values(headerMap);
  Object.values(headerMap).forEach((label) => {
    exportData[0][label] = '';
  });
  const worksheet = XLSX.utils.json_to_sheet(exportData, { header: headers });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(
    workbook,
    filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`,
  );
};

export interface XlsxListValidation {
  /** A1 범위 (예: K2:K501) */
  sqref: string;
  options: string[];
  allowBlank?: boolean;
  errorTitle?: string;
  errorMessage?: string;
  promptTitle?: string;
  promptMessage?: string;
}

const escapeXml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const buildDataValidationsXml = (validations: XlsxListValidation[]): string =>
  `<dataValidations count="${validations.length}">${validations
    .map((item) => {
      const allowBlank = item.allowBlank === false ? '0' : '1';
      const attrs = [
        'type="list"',
        `allowBlank="${allowBlank}"`,
        'showInputMessage="1"',
        'showErrorMessage="1"',
        'errorStyle="stop"',
        `sqref="${escapeXml(item.sqref)}"`,
      ];
      if (item.errorTitle) {
        attrs.push(`errorTitle="${escapeXml(item.errorTitle)}"`);
      }
      if (item.errorMessage) {
        attrs.push(`error="${escapeXml(item.errorMessage)}"`);
      }
      if (item.promptTitle) {
        attrs.push(`promptTitle="${escapeXml(item.promptTitle)}"`);
      }
      if (item.promptMessage) {
        attrs.push(`prompt="${escapeXml(item.promptMessage)}"`);
      }
      const formula = `"${item.options.join(',')}"`;
      return `<dataValidation ${attrs.join(' ')}><formula1>${escapeXml(
        formula,
      )}</formula1></dataValidation>`;
    })
    .join('')}</dataValidations>`;

/**
 * dataValidations 를 OOXML 스키마 순서에 맞게 넣는다.
 * SheetJS 시트는 sheetData 뒤에 ignoredErrors 가 있어, 파일 끝에 넣으면
 * Excel이 "파일이 손상되었습니다"로 거부한다.
 */
const insertDataValidationsXml = (
  sheetXml: string,
  dataValidationsXml: string,
): string => {
  if (/<dataValidations[\s\S]*<\/dataValidations>/.test(sheetXml)) {
    return sheetXml.replace(
      /<dataValidations[\s\S]*<\/dataValidations>/,
      dataValidationsXml,
    );
  }

  if (sheetXml.includes('</sheetData>')) {
    return sheetXml.replace(
      '</sheetData>',
      `</sheetData>${dataValidationsXml}`,
    );
  }

  return sheetXml.replace(
    /<\/((?:[\w]+:)?worksheet)>/,
    `${dataValidationsXml}</$1>`,
  );
};

/**
 * SheetJS 커뮤니티 버전은 dataValidation 쓰기를 지원하지 않아
 * worksheet XML에 리스트 검증(콤보박스)을 직접 삽입한다.
 * OOXML의 showDropDown=1 은 드롭다운을 숨기므로 속성을 넣지 않는다.
 */
export const injectWorksheetListValidations = (
  sheetXml: string,
  validations: XlsxListValidation[],
): string => {
  if (validations.length === 0 || !sheetXml.includes('worksheet')) {
    return sheetXml;
  }

  return insertDataValidationsXml(
    sheetXml,
    buildDataValidationsXml(validations),
  );
};

const downloadBlobFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** 헤더 템플릿 + 지정 열 콤보박스(리스트 검증) 바이너리 */
export const buildXlsxTemplateWithListValidations = async (
  headerMap: Record<string, string>,
  validations: XlsxListValidation[] = [],
): Promise<ArrayBuffer> => {
  const exportData: Record<string, string>[] = [{}];
  const headers = Object.values(headerMap);
  headers.forEach((label) => {
    exportData[0][label] = '';
  });
  const worksheet = XLSX.utils.json_to_sheet(exportData, { header: headers });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  const xlsxBuffer = XLSX.write(workbook, {
    type: 'array',
    bookType: 'xlsx',
  });
  const zip = await JSZip.loadAsync(xlsxBuffer);
  const sheetPath = Object.keys(zip.files).find(
    (name) => name.startsWith('xl/worksheets/sheet') && name.endsWith('.xml'),
  );

  if (sheetPath && validations.length > 0) {
    const sheetXml = await zip.file(sheetPath)?.async('string');
    if (sheetXml) {
      zip.file(
        sheetPath,
        injectWorksheetListValidations(sheetXml, validations),
      );
    }
  }

  return zip.generateAsync({
    type: 'arraybuffer',
    mimeType: XLSX_MIME,
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
};

/** 헤더 템플릿 + 지정 열 콤보박스(리스트 검증) */
export const exportXlsxTemplateWithListValidations = async (
  headerMap: Record<string, string>,
  filename = 'template',
  validations: XlsxListValidation[] = [],
) => {
  const bytes = await buildXlsxTemplateWithListValidations(
    headerMap,
    validations,
  );
  downloadBlobFile(new Blob([bytes], { type: XLSX_MIME }), filename);
};

export interface ImportXlsxOptions {
  validateOnlyRequiredHeaders?: boolean;
}

/** XLSX 파일을 headerMap 기준 객체 배열로 파싱 */
export const importFromXlsx = async (
  file: File | Blob | ArrayBuffer,
  headerMap: Record<string, string>,
  options: ImportXlsxOptions = {},
): Promise<Record<string, unknown>[]> => {
  let arrayBuffer: ArrayBuffer;
  if (file instanceof ArrayBuffer) {
    arrayBuffer = file;
  } else if (file instanceof Blob) {
    arrayBuffer = await file.arrayBuffer();
  } else {
    throw new Error('지원하지 않는 파일 타입입니다.');
  }

  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  const rawData = XLSX.utils.sheet_to_json<(string | number | null)[]>(
    worksheet,
    {
      header: 1,
      defval: null,
      blankrows: false,
    },
  );

  const [headerRow, ...dataRows] = rawData;
  if (!headerRow) throw new Error('헤더가 없습니다.');

  const expectedHeaders = Object.values(headerMap);
  const requiredHeaders = options.validateOnlyRequiredHeaders
    ? expectedHeaders.filter((label) => label.endsWith('*'))
    : expectedHeaders;
  const missingHeaders = requiredHeaders.filter(
    (label) => !headerRow.includes(label),
  );
  if (missingHeaders.length > 0) {
    throw new Error(`필수 필드가 누락되었습니다: ${missingHeaders.join(', ')}`);
  }
  if (!options.validateOnlyRequiredHeaders) {
    const extraHeaders = headerRow.filter(
      (label) => !expectedHeaders.includes(String(label)),
    );
    if (extraHeaders.length > 0) {
      throw new Error(
        `잘못된 필드가 포함되어 있습니다: ${extraHeaders.join(', ')}`,
      );
    }
  }

  return dataRows.map((row) => {
    const mapped: Record<string, unknown> = {};
    Object.entries(headerMap).forEach(([key, label]) => {
      const colIdx = headerRow.indexOf(label);
      mapped[key] = colIdx === -1 ? null : row[colIdx];
    });
    return mapped;
  });
};

export const MAX_MATCH_EXCEL_HEADER_MAP: Record<string, string> = {
  email: '사용자 이메일',
  clinicName: '병의원',
  prescriptionYearMonth: '처방년월',
  settlementYearMonth: '정산 년월',
  pharmaCompanyNameOrig: '제약사',
  drugCodeOrig: '의약품코드',
  drugNameOrig: '의약품명',
  outHospitalQuantity: '처방량',
  priceOrig: '약가',
  totalAmount: '처방 금액',
  standardCommissionRateOrig: '수수료율(%)',
  commissionAmountOrig: '예상 금액',
  prescriptionApprovalStatus: '처방 승인 상태',
  finalAmount: '확정 금액',
  pharmaCompanySettlementStatus: '정산 상태',
  pharmaCompanyNameNew: 'M_제약사',
  drugCodeNew: 'M_의약품코드',
  drugNameNew: 'M_의약품명',
  standardCommissionRateNew: 'M_수수료율(%)',
  commissionAmountNew: 'M_예상 금액',
  maxMatchApprovalStatus: '제안',
  transactionStatus: '필터링 상태',
  remarks: '비고',
};

/**
 * 맥스매치 Lite(mini) 엑셀 컬럼 — MaxMatchMiniExcelDetail 기준.
 * Full 다운로드도 동일 양식(mapMaxMatchLiteExcelRows).
 * Full V121+: priceOrig·totalAmount·요율 스냅샷 포함.
 */
export const MAX_MATCH_LITE_EXCEL_HEADER_MAP: Record<string, string> = {
  email: '사용자 이메일',
  clinicName: '병의원',
  prescriptionYearMonth: '처방년월',
  pharmaCompanyNameOrig: '제약사',
  drugCodeOrig: '의약품코드',
  drugNameOrig: '의약품명',
  outHospitalQuantity: '처방량',
  priceOrig: '약가',
  totalAmount: '처방 금액',
  standardCommissionRateOrig: '수수료율(%)',
  commissionAmountOrig: '예상 금액',
  pharmaCompanyNameNew: 'M_제약사',
  drugCodeNew: 'M_의약품코드',
  drugNameNew: 'M_의약품명',
  priceNew: 'M_약가',
  totalAmountNew: 'M_처방 금액',
  standardCommissionRateNew: 'M_수수료율(%)',
  commissionAmountNew: 'M_예상 금액',
  status: '상태',
  createdDate: '등록일시',
};

/** @deprecated Lite 헤더와 동일 — MAX_MATCH_LITE_EXCEL_HEADER_MAP 사용 */
export const MAX_MATCH_MINI_EXCEL_HEADER_MAP = MAX_MATCH_LITE_EXCEL_HEADER_MAP;

/** 맥스매치 운영 결과 엑셀 컬럼 */
export const MAX_MATCH_OPS_EXCEL_HEADER_MAP: Record<string, string> = {
  createdAdminEmail: '생성계정',
  pharmaCompanyName: '제공업체',
  clinicName: '병의원',
  refYm: '처방년월',
  pharmaCompanyNameOrig: '제약사',
  drugCodeOrig: '의약품코드',
  drugNameOrig: '의약품명',
  outHospitalQuantity: '처방량',
  priceOrig: '약가',
  totalAmount: '처방 금액',
  standardCommissionRateOrig: '수수료율(%)',
  commissionAmountOrig: '예상 금액',
  pharmaCompanyNameNew: 'M_제약사',
  drugCodeNew: 'M_의약품코드',
  drugNameNew: 'M_의약품명',
  priceNew: 'M_약가',
  standardCommissionRateNew: 'M_수수료율(%)',
  commissionAmountNew: 'M_예상 금액',
  status: '상태',
  createdDate: '등록일시',
};
